import { Shadows } from '@zapier/design-system';
import { createContext } from 'react';
import type { ContextValue } from 'react-aria-components';
import {
  Popover as ReactAriaPopover,
  PopoverProps as ReactAriaPopoverProps,
  useContextProps,
} from 'react-aria-components';
import { mergeClassNames } from '../utils/mergeClassNames';
import styles from './Popover.module.scss';
import { SelectBaseProps } from './SelectBase';

export type PopoverProps = ReactAriaPopoverProps & {
  /**
   * The size, impacting the popover's dimensions.
   * @default 'medium'
   */
  size?: SelectBaseProps['size'];
};

export type PopoverContextValue = ContextValue<
  Omit<PopoverProps, 'children'>,
  HTMLDivElement
>;

export const PopoverContext = createContext<PopoverContextValue>({});

/**
 * A styled Popover from `react-aria-components`. Useful when creating a custom Select.
 *
 * Merges context props from `PopoverContext`.
 */
export const Popover = (props: PopoverProps) => {
  const [contextProps] = useContextProps(props, null, PopoverContext);
  const { className, size = 'medium', style, ...otherProps } = contextProps;

  return (
    <ReactAriaPopover
      {...otherProps}
      style={{
        ...style,
        '--popover-shadow': Shadows.elevation20,
      }}
      className={(renderProps) =>
        mergeClassNames([styles.popover, className], renderProps)
      }
      data-size={size}
    />
  );
};
